import { useEffect, useMemo, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Search, Check, Ban, Pencil } from "lucide-react";
import {
  listPrimeContactsForReview,
  verifyPrimeContact,
  rejectPrimeContact,
  setPrimeContactEmailManual,
  type PrimeContactForReview,
} from "./api/CrmApi";
import PageHeader from "shared/PageHeader";
import Button from "shared/Button";
import StatusPill from "shared/StatusPill";
import Alert from "shared/Alert";
import { FILLED_INPUT, FORM_CONTROL } from "shared/tableStyles";
import PagerNav from "shared/PagerNav";
import { useUrlState, useUrlNumberState, useUpdateEffect, rowLinkState } from "shared/listNavigation";

function ConfidenceBadge({ confidence }: { confidence: PrimeContactForReview["confidence"] }) {
  if (confidence === "high") return <StatusPill tone="success" dot>HIGH</StatusPill>;
  if (confidence === "medium") return <StatusPill tone="warning" dot>MEDIUM</StatusPill>;
  return <StatusPill tone="neutral" dot>LOW</StatusPill>;
}

function ManualEntryRow({
  primeId,
  onSaved,
  onError,
}: {
  primeId: number;
  onSaved: (updated: PrimeContactForReview) => void;
  onError: (message: string) => void;
}) {
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState<"primary" | "secondary" | null>(null);

  async function submit(slot: "primary" | "secondary") {
    if (!value.trim()) return;
    setSaving(slot);
    try {
      const updated = await setPrimeContactEmailManual(primeId, value.trim(), slot);
      onSaved(updated as PrimeContactForReview);
    } catch (err: any) {
      onError(err?.response?.data?.detail || err?.message || "Failed to save that email.");
      setSaving(null);
    }
  }

  return (
    <div className="flex items-center gap-2">
      <input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Found a different email? Enter it here..."
        className={`flex-1 px-3 py-1.5 ${FORM_CONTROL}`}
      />
      <Button
        variant="primary"
        size="sm"
        onClick={() => submit("primary")}
        disabled={!value.trim() || saving !== null}
        className="whitespace-nowrap"
      >
        {saving === "primary" ? "Saving…" : "Set as Primary"}
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => submit("secondary")}
        disabled={!value.trim() || saving !== null}
        className="whitespace-nowrap"
      >
        {saving === "secondary" ? "Saving…" : "Set as Secondary"}
      </Button>
    </div>
  );
}

function ReviewCard({
  prime,
  crmBasePath,
  onSettled,
  onUpdated,
}: {
  prime: PrimeContactForReview;
  crmBasePath: string;
  onSettled: (primeId: number) => void;
  onUpdated: (updated: PrimeContactForReview) => void;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showManual, setShowManual] = useState(false);
  const location = useLocation();
  const linkState = rowLinkState(location);
  const name = [prime.first_name, prime.last_name].filter(Boolean).join(" ");

  // What the human is actually being asked to approve/reject: the
  // candidate address, whichever slot it's currently sitting in.
  const candidate = prime.confidence === "high" ? prime.primary_email : prime.secondary_email;
  const original = prime.confidence === "high" ? prime.secondary_email : prime.primary_email;

  async function doVerify() {
    setSaving(true);
    setError(null);
    try {
      await verifyPrimeContact(prime.id);
      onSettled(prime.id);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || "Failed to verify. Please try again.");
      setSaving(false);
    }
  }

  async function doReject() {
    setSaving(true);
    setError(null);
    try {
      await rejectPrimeContact(prime.id);
      onSettled(prime.id);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || "Failed to reject. Please try again.");
      setSaving(false);
    }
  }

  return (
    <div className="rounded-xl border border-border bg-surface p-4 flex flex-col gap-3">
      <div className="flex items-center gap-2.5 flex-wrap">
        <ConfidenceBadge confidence={prime.confidence} />
        {prime.prime_company_id ? (
          <Link to={`${crmBasePath}/companies/${prime.prime_company_id}`} state={linkState} className="text-sm font-semibold text-primary hover:underline">
            {prime.company_name || `Company #${prime.prime_company_id}`}
          </Link>
        ) : (
          <span className="text-sm font-semibold text-foreground">(no linked company)</span>
        )}
        {name && <span className="text-xs text-muted-foreground">({name})</span>}
        <Link to={`${crmBasePath}/prime-contacts/${prime.id}`} state={linkState} className="text-xs text-muted-foreground hover:text-foreground ml-auto">
          Full history
        </Link>
      </div>

      <div className="grid gap-1 text-sm">
        <div>
          <span className="text-muted-foreground">Candidate: </span>
          <span className="text-foreground font-mono">{candidate}</span>
        </div>
        <div>
          <span className="text-muted-foreground">Current/original: </span>
          <span className="text-foreground">{original}</span>
        </div>
        {prime.review_evidence && (
          <div>
            <span className="text-muted-foreground">Basis: </span>
            <span className="text-foreground">{prime.review_evidence}</span>
          </div>
        )}
      </div>

      {error && (
        <Alert tone="danger">
          {error}
        </Alert>
      )}

      <div className="flex items-center gap-2">
        <Button variant="primary" size="sm" onClick={doVerify} disabled={saving}>
          <Check className="w-3.5 h-3.5" /> Verify
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={doReject}
          disabled={saving}
          className="text-destructive border-destructive-border hover:bg-destructive-soft"
        >
          <Ban className="w-3.5 h-3.5" /> Reject
        </Button>
        <Button variant="outline" size="sm" onClick={() => setShowManual((v) => !v)} disabled={saving} className="ml-auto">
          <Pencil className="w-3.5 h-3.5" /> Found a different email?
        </Button>
      </div>

      {showManual && (
        <ManualEntryRow
          primeId={prime.id}
          onSaved={(updated) => {
            setShowManual(false);
            if (updated.human_review_status === "verified") {
              onSettled(prime.id);
            } else {
              onUpdated(updated);
            }
          }}
          onError={setError}
        />
      )}
    </div>
  );
}

export default function EmailReviewQueuePage() {
  const location = useLocation();
  const crmBasePath = location.pathname.startsWith("/employee") ? "/employee/crm" : "/admin/crm";

  const [items, setItems] = useState<PrimeContactForReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Page + filters live in the URL so returning from a company/contact page
  // (or via browser back) lands on the same slice of the queue.
  const [page, setPage] = useUrlNumberState("page", 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [search, setSearch] = useUrlState("q", "");
  const [searchInput, setSearchInput] = useState(search);
  const [confidence, setConfidence] = useUrlState<"" | "high" | "medium" | "low">("confidence", "");

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput, setSearch]);

  useUpdateEffect(() => {
    setPage(1);
  }, [search, confidence]);

  const reload = useMemo(
    () => () => {
      setLoading(true);
      setError("");
      listPrimeContactsForReview({
        page,
        page_size: 20,
        confidence: confidence || undefined,
        search: search || undefined,
      })
        .then((res) => {
          setItems(res.items);
          setTotalCount(res.total_count);
          setTotalPages(res.total_pages);
        })
        .catch((e) => setError(e.response?.data?.detail || "Failed to load the review queue"))
        .finally(() => setLoading(false));
    },
    [page, search, confidence]
  );

  useEffect(() => {
    reload();
  }, [reload]);

  function removeFromQueue(primeId: number) {
    setItems((prev) => prev.filter((p) => p.id !== primeId));
    setTotalCount((c) => Math.max(0, c - 1));
  }

  function updateInPlace(updated: PrimeContactForReview) {
    setItems((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
  }

  return (
    <div className="flex flex-col gap-4 h-full min-h-0">
      <PageHeader
        title="Email Review Queue"
        badge={<StatusPill tone="neutral" size="sm">{totalCount} pending</StatusPill>}
        description="Named-email candidates from /rfp-primes research, awaiting a human decision. Verify approves the candidate as the live outreach address; Reject falls back to the safe original. Both are remembered permanently on this record — nothing gets re-proposed once decided."
      />

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search className="w-4 h-4 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search email or name..."
            className={`pl-8 pr-3 py-2 w-64 ${FILLED_INPUT}`}
          />
        </div>
        <select
          value={confidence}
          onChange={(e) => setConfidence(e.target.value as "" | "high" | "medium" | "low")}
          className={`px-3 py-2 ${FORM_CONTROL}`}
        >
          <option value="">All confidence</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>

      {error && <Alert tone="danger">{error}</Alert>}

      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-3">
        {items.map((p) => (
          <ReviewCard key={p.id} prime={p} crmBasePath={crmBasePath} onSettled={removeFromQueue} onUpdated={updateInPlace} />
        ))}
        {!loading && items.length === 0 && (
          <div className="rounded-xl border border-dashed border-border bg-surface px-4 py-10 text-center text-sm text-muted-foreground">
            Nothing pending review{confidence || search ? " for these filters" : ""}.
          </div>
        )}
        {loading && (
          <div className="flex items-center justify-center py-10">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>

      <PagerNav page={page} totalPages={totalPages} onChange={setPage} />
    </div>
  );
}
